/**
 * bloomreach-facets-drawer.js
 *
 * Bloomreach's Loomi AI search widget renders its filters as a horizontal
 * row of dropdown chips (br-facet / br-slider-facet custom elements) plus
 * an "All filters" overflow button, inside .br-toolbar__filters-drawer-body.
 *
 * This turns that same wrapper into a left-hand slide-in drawer that looks
 * like the theme's native facet drawer.
 *
 * IMPORTANT: earlier versions of this script physically moved the br-facet /
 * br-slider-facet elements into a brand new container. That broke filtering
 * — moving a web component to a new parent forces a disconnect/reconnect,
 * and Bloomreach's rendering keeps internal references tied to the original
 * container, so the moved copies stopped talking to the search backend.
 *
 * This version never reparents anything Bloomreach rendered. It only:
 *   - adds a class to .br-toolbar__filters-drawer-body so CSS turns it into
 *     an off-canvas panel (fixed position + transform, nothing removed)
 *   - prepends a small header (title + close button) as a new first child
 *   - appends a separate overlay + trigger button elsewhere in the page
 * All of Bloomreach's own nodes stay exactly where they were rendered, with
 * their original parent chain intact, so their event wiring keeps working.
 *
 * Bloomreach replaces the whole .br-toolbar__filters-drawer-body node
 * (rather than just updating its children) whenever a filter is applied, so
 * the toolbar we patched a moment ago gets swapped out for a fresh,
 * un-patched one mid-interaction. Previously that made activate() build a
 * brand new header/overlay/trigger on top of the old ones every time —
 * the old overlay never got cleaned up (stuck visible), while the new,
 * unpatched toolbar looked "closed". This version keeps a single overlay
 * and trigger button for the whole page lifetime and just re-wires them to
 * whichever toolbar node currently exists, carrying the open/closed state
 * across the swap instead of resetting it.
 *
 * DEBUG MODE
 * Add ?brfacetdebug=1 to the URL (or run
 * localStorage.setItem('br_facet_debug', '1') once in the console) to log
 * every step under the "[BR-Facets-Drawer]" tag and show a small on-page
 * status panel. Turn off with localStorage.removeItem('br_facet_debug').
 */
(function () {
  var DRAWER_CLASS = 'br-facet-drawer';
  var ACTIVE_CLASS = 'br-facet-drawer--active';
  var OVERLAY_ACTIVE_CLASS = 'br-facet-drawer__overlay--active';
  var OBSERVE_TIMEOUT = 20000;

  // Populated by layout/theme.liquid from the theme's own locale files
  // ({{ 'products.facets.filter_button' | t }} etc.) so this drawer speaks
  // whatever language the storefront is in, same as the native one. Falls
  // back to English if that inline script didn't run for some reason.
  var STRINGS = Object.assign(
    { filterButton: 'Filter', apply: 'Apply', resetFilters: 'Reset filters' },
    window.BRFacetDrawerStrings || {}
  );

  var DEBUG =
    /[?&]brfacetdebug=1/.test(window.location.search) ||
    (function () {
      try {
        return window.localStorage.getItem('br_facet_debug') === '1';
      } catch (e) {
        return false;
      }
    })();

  var panel = null;

  function log(message) {
    if (!DEBUG) return;
    console.log('[BR-Facets-Drawer] ' + message);
    updatePanel(message);
  }

  function warn(message, data) {
    console.warn('[BR-Facets-Drawer] ' + message, data || '');
    if (DEBUG) updatePanel('⚠ ' + message);
  }

  function ensurePanel() {
    if (!DEBUG || panel) return;
    panel = document.createElement('div');
    panel.id = 'br-facet-debug-panel';
    panel.style.cssText =
      'position:fixed;bottom:0;left:0;z-index:999999;max-width:360px;max-height:40vh;' +
      'overflow:auto;background:rgba(0,0,0,0.85);color:#0f0;font:11px/1.4 monospace;' +
      'padding:8px 10px;white-space:pre-wrap;pointer-events:none;';
    panel.textContent = '[BR-Facets-Drawer] debug mode on\n';
    document.documentElement.appendChild(panel);
  }

  function updatePanel(line) {
    if (!DEBUG) return;
    ensurePanel();
    panel.textContent += '[' + new Date().toLocaleTimeString() + '] ' + line + '\n';
    panel.scrollTop = panel.scrollHeight;
  }

  // ---- Singletons: created once, reused across every toolbar swap ----
  var isOpen = false;
  var overlay = null;
  var triggerWrap = null;
  var triggerButton = null;
  var currentToolbar = null;
  var currentFooter = null;
  var currentEscHandler = null;

  // Which individual facet (by its visible title, e.g. "Kleur") is expanded
  // right now, so we can re-expand the same one after Bloomreach swaps the
  // whole toolbar out for a fresh copy on every filter click — otherwise the
  // dropdown you just opened collapses the moment you check an option.
  var openFacetLabel = null;
  var facetObservers = [];
  var shadowRootWarned = false;

  function disconnectFacetObservers() {
    facetObservers.forEach(function (obs) {
      obs.disconnect();
    });
    facetObservers = [];
  }

  // br-facet's internals (header/arrow/title/dropdown) live inside its own
  // shadow root, so regular querySelector from the light DOM can't see them
  // — has to go through element.shadowRoot, which only works if Bloomreach
  // attached it in "open" mode. If it's closed, we just skip this feature
  // (the drawer + filtering keep working either way, this only handles the
  // "stay expanded" nicety).
  function trackFacetOpenState(toolbar) {
    disconnectFacetObservers();

    var facets = toolbar.querySelectorAll('br-facet');
    facets.forEach(function (facetEl) {
      var root = facetEl.shadowRoot;
      if (!root) {
        if (!shadowRootWarned) {
          shadowRootWarned = true;
          warn('br-facet.shadowRoot is not accessible (closed shadow root) — cannot keep facets expanded across filter clicks');
        }
        return;
      }

      var titleEl = root.querySelector('.br-facet__title');
      var label = titleEl ? titleEl.textContent.trim() : null;
      if (!label) return;

      var obs = new MutationObserver(function () {
        var arrow = root.querySelector('.br-facet__arrow');
        if (!arrow) return;
        if (arrow.classList.contains('br-facet__arrow--open')) {
          openFacetLabel = label;
          log('tracking open facet: ' + label);
        } else if (openFacetLabel === label) {
          openFacetLabel = null;
          log('facet closed: ' + label);
        }
      });
      obs.observe(root, { attributes: true, attributeFilter: ['class'], subtree: true });
      facetObservers.push(obs);
    });
  }

  function reopenTrackedFacet(toolbar) {
    if (!openFacetLabel) return;

    var facets = toolbar.querySelectorAll('br-facet');
    for (var i = 0; i < facets.length; i++) {
      var root = facets[i].shadowRoot;
      if (!root) return;

      var titleEl = root.querySelector('.br-facet__title');
      if (titleEl && titleEl.textContent.trim() === openFacetLabel) {
        var header = root.querySelector('.br-facet__header');
        if (header) {
          header.click();
          log('re-opened facet after toolbar swap: ' + openFacetLabel);
        }
        return;
      }
    }
  }

  function buildTrigger() {
    var wrap = document.createElement('div');
    wrap.className = 'facet-drawer-button-wrap br-facet-drawer-trigger-wrap';
    wrap.innerHTML =
      '<button type="button" class="btn btn--primary gap-2 facet-drawer-button" aria-expanded="false">' +
      '<span class="btn__text flex gap-1 items-center">' +
      '<svg width="19" height="12" viewBox="0 0 19 12" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">' +
      '<path d="M0.875 0.875H17.5775" stroke="currentColor" stroke-width="1.75" stroke-miterlimit="10" stroke-linecap="square"/>' +
      '<path d="M2.875 5.875H14.875" stroke="currentColor" stroke-width="1.75" stroke-miterlimit="10" stroke-linecap="square"/>' +
      '<path d="M4.875 10.875H12.875" stroke="currentColor" stroke-width="1.75" stroke-miterlimit="10" stroke-linecap="square"/>' +
      '</svg>' +
      '<span></span>' +
      '</span>' +
      '</button>';
    wrap.querySelector('span > span').textContent = STRINGS.filterButton;
    var button = wrap.querySelector('button');
    button.addEventListener('click', function (event) {
      event.preventDefault();
      isOpen ? closeDrawer() : openDrawer();
    });
    return { wrap: wrap, button: button };
  }

  function buildHeader() {
    var header = document.createElement('div');
    header.className = 'br-facet-drawer__header';
    header.innerHTML =
      '<p class="br-facet-drawer__heading h4"><span></span></p>' +
      '<button type="button" class="br-facet-drawer__close" aria-label="Close">' +
      '<svg xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false" class="icon icon-close icon--medium icon--thick" fill="none" viewBox="0 0 24 24"><use href="#icon-close"/></svg>' +
      '</button>';
    header.querySelector('.br-facet-drawer__heading span').textContent = STRINGS.filterButton;
    header.querySelector('.br-facet-drawer__close').addEventListener('click', function (event) {
      event.preventDefault();
      closeDrawer();
    });
    return header;
  }

  function buildFooter() {
    var footer = document.createElement('div');
    footer.className = 'drawer__sticky drawer__sticky--bottom w-full z-1 br-facet-drawer__footer';
    footer.innerHTML =
      '<div class="br-facet-drawer__footer-reset-slot"></div>' +
      '<button type="button" class="btn btn--primary w-full br-facet-drawer__apply">' +
      '<span class="btn__text"></span>' +
      '</button>';
    footer.querySelector('.br-facet-drawer__apply .btn__text').textContent = STRINGS.apply;
    footer.querySelector('.br-facet-drawer__apply').addEventListener('click', function (event) {
      event.preventDefault();
      // Bloomreach filters live as you check options, there's no separate
      // "submit" step — this button's only job is to close the drawer,
      // same as the native Apply button once its request resolves.
      closeDrawer();
    });
    return footer;
  }

  // Bloomreach's real "Clear all filters" button (.br-facets__clear-all)
  // stays exactly where Bloomreach put it — you asked to keep it visible
  // in its original spot too, so instead of moving it we build our own
  // look-alike button for the footer whose only job is to forward a click
  // to the real one. That keeps a single source of truth for the actual
  // reset behavior (whatever Bloomreach's handler does) while giving the
  // drawer its own native-styled, localized Reset button.
  var currentClearAllButton = null;

  function buildResetProxyButton() {
    var button = document.createElement('button');
    button.type = 'button';
    button.className = 'btn btn--secondary facet-reset br-facet-drawer__reset-proxy';
    button.innerHTML = '<span class="btn__text"></span>';
    button.querySelector('.btn__text').textContent = STRINGS.resetFilters;
    button.addEventListener('click', function (event) {
      event.preventDefault();
      if (currentClearAllButton && document.body.contains(currentClearAllButton)) {
        currentClearAllButton.click();
        log('forwarded click to Bloomreach\'s "Clear all filters" button');
      } else {
        warn('no live "Clear all filters" button found to forward the click to');
      }
    });
    return button;
  }

  function syncResetProxy() {
    if (!currentFooter) return;
    var slot = currentFooter.querySelector('.br-facet-drawer__footer-reset-slot');
    if (!slot) return;

    var hasLiveButton = currentClearAllButton && document.body.contains(currentClearAllButton);
    if (hasLiveButton && !slot.firstChild) {
      slot.appendChild(buildResetProxyButton());
    } else if (!hasLiveButton) {
      slot.innerHTML = '';
    }
  }

  // Bloomreach only renders .br-facets__clear-all once at least one filter
  // is active, and not necessarily as a descendant of
  // .br-toolbar__filters-drawer-body — so keep watching the whole document
  // for it to appear/disappear instead of a single check right after the
  // toolbar swap.
  var clearAllObserver = null;

  function watchForClearAllButton() {
    if (clearAllObserver) return; // only ever need one of these, page-wide

    function check() {
      var button = document.querySelector('.br-facets__clear-all');
      if (button !== currentClearAllButton) {
        currentClearAllButton = button;
        log(button ? 'found Bloomreach\'s "Clear all filters" button' : '"Clear all filters" button no longer present');
      }
      syncResetProxy();
    }

    check();
    clearAllObserver = new MutationObserver(check);
    clearAllObserver.observe(document.body, { childList: true, subtree: true });
  }

  function buildOverlay() {
    var el = document.createElement('div');
    // Reuse the theme's own .fixed-overlay (same dim backdrop + transition
    // as every other native drawer/modal) alongside our own modifier class,
    // since we don't have the ".drawer[active] > .fixed-overlay" structural
    // relationship the native drawers rely on to toggle visibility.
    el.className = 'fixed-overlay br-facet-drawer__overlay';
    el.addEventListener('click', closeDrawer);
    return el;
  }

  function openDrawer() {
    if (!currentToolbar) return;
    isOpen = true;
    currentToolbar.classList.add(ACTIVE_CLASS);
    overlay.classList.add(OVERLAY_ACTIVE_CLASS);
    triggerButton.setAttribute('aria-expanded', 'true');
    currentEscHandler = function (event) {
      if (event.code === 'Escape') closeDrawer();
    };
    document.addEventListener('keyup', currentEscHandler);
    log('drawer opened');
  }

  function closeDrawer() {
    isOpen = false;
    if (currentToolbar) currentToolbar.classList.remove(ACTIVE_CLASS);
    overlay.classList.remove(OVERLAY_ACTIVE_CLASS);
    triggerButton.setAttribute('aria-expanded', 'false');
    if (currentEscHandler) {
      document.removeEventListener('keyup', currentEscHandler);
      currentEscHandler = null;
    }
    log('drawer closed');
  }

  // Called for the very first toolbar seen, and again every time Bloomreach
  // swaps in a replacement toolbar node — never creates a second overlay/
  // trigger, just re-homes the existing ones and carries isOpen across.
  function activate(toolbar) {
    if (toolbar === currentToolbar) return; // same node, nothing to do

    try {
      if (!overlay) {
        overlay = buildOverlay();
        document.body.appendChild(overlay);
      }
      if (!triggerWrap) {
        var triggerParts = buildTrigger();
        triggerWrap = triggerParts.wrap;
        triggerButton = triggerParts.button;
      }

      currentToolbar = toolbar;
      toolbar.classList.add(DRAWER_CLASS);
      toolbar.insertBefore(buildHeader(), toolbar.firstChild);
      toolbar.parentNode.insertBefore(triggerWrap, toolbar);

      currentFooter = buildFooter();
      toolbar.appendChild(currentFooter);
      syncResetProxy();
      watchForClearAllButton();

      if (isOpen) {
        // Drawer was open when the previous toolbar got swapped out (e.g.
        // right after checking a filter option) — keep it open on the new
        // one instead of it silently reverting to closed.
        toolbar.classList.add(ACTIVE_CLASS);
        log('re-activated on a replacement toolbar node, kept drawer open');
      } else {
        log('activated drawer on .br-toolbar__filters-drawer-body (no Bloomreach nodes moved)');
      }

      // Give the freshly-inserted br-facet elements a tick to render their
      // shadow DOM content before we try to read/re-open them.
      window.requestAnimationFrame(function () {
        reopenTrackedFacet(toolbar);
        trackFacetOpenState(toolbar);
      });
    } catch (err) {
      warn('activate() threw an error', err);
      console.error(err);
    }
  }

  function boot() {
    log('script loaded, watching for the Bloomreach facets toolbar...');
    ensurePanel();

    var toolbarNow = document.querySelector('.br-toolbar__filters-drawer-body');
    if (toolbarNow) {
      log('toolbar already present on load');
      activate(toolbarNow);
    } else {
      log('toolbar not present yet, starting MutationObserver on document.body');
    }

    var settled = !!toolbarNow;
    var observer = new MutationObserver(function () {
      var toolbar = document.querySelector('.br-toolbar__filters-drawer-body');
      if (!toolbar) return;

      if (!settled) {
        settled = true;
        log('toolbar detected via MutationObserver');
      }
      // Keep observing (rather than disconnecting): Bloomreach replaces the
      // toolbar node itself on every filter interaction, so we need to
      // keep re-patching whatever the current node is for the life of the
      // page.
      activate(toolbar);
    });
    observer.observe(document.body, { childList: true, subtree: true });

    setTimeout(function () {
      if (settled) return;
      warn(
        'gave up after ' + OBSERVE_TIMEOUT / 1000 + 's — .br-toolbar__filters-drawer-body never appeared. ' +
          'Either Bloomreach isn\'t rendering on this page, or its class name has changed.'
      );
    }, OBSERVE_TIMEOUT);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
